import { useMemo, useState } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import { isEmpty } from "lodash"
import { TextField, IconButton, Tooltip, InputAdornment } from "@mui/material"
import { Close } from "@mui/icons-material"
import FactorConstraintInput from "components/factor-constraint-input"
import { SMART_TRADE_RULE } from "helpers/shapes"

export default function Rule({ rule, removeSmartTradeRule, updateSmartTradeRule, currentValue, isReadOnly, smartLabel }) {
  const [minValue, setMinValue] = useState(Number.isFinite(parseFloat(rule.min)) ? rule.min : "")
  const [maxValue, setMaxValue] = useState(Number.isFinite(parseFloat(rule.max)) ? rule.max : "")

  function onRemoveSmartTradeRule() {
    removeSmartTradeRule(rule)
  }

  function onUpdateSmartTradeRule({ id, value: inputValue }) {
    let min = ""
    let max = ""

    let value = inputValue
    const floatValue = parseFloat(value)
    value = value && Number.isFinite(floatValue) ? floatValue : value

    if (id === "min") {
      min = value
      max = maxValue
    } else {
      max = value
      min = minValue
    }
    updateSmartTradeRule({ ...rule, min, max })
    setMinValue(min)
    setMaxValue(max)
  }

  const title = useMemo(() =>
    `${rule.name}${currentValue === "-" ? `\n No current smart ${smartLabel} value` : `\n Current smart ${smartLabel} value: ${currentValue}`}`,
  [rule, currentValue, smartLabel])

  return <div className="rule">
    <div className={classNames("rule-form", { "max-only": rule.maxOnly })}>
      <div className="rule-name-wrapper">
        {!isReadOnly && <IconButton aria-label="Remove" onClick={onRemoveSmartTradeRule} size="large">
          <Close fontSize="small" />
        </IconButton>}
        <Tooltip
          title={title}
          classes={{ popper: "rule-name-tooltip-popper", tooltip: "rule-name-tooltip" }}
        >
          <div className="rule-name">
            {rule.name}
            <div className="rule-type">{rule.info}</div>
          </div>
        </Tooltip>
      </div>
      <div className="current-value">
        {currentValue}
      </div>
      <div className="input-wrapper">
        <div className="input-wrapper-row">
          <div className="minimum">
            <div className="rule-input">
              {rule.category === "factor"
                ? <FactorConstraintInput {...{
                    id: "min",
                    autoFocus: true,
                    initialValue: minValue,
                    onChange: onUpdateSmartTradeRule,
                    supportMultiplierSuffix: rule.isCurrency,
                  }}
                />
                : <TextField
                    variant="standard"
                    id="min"
                    autoFocus
                    disabled={isReadOnly}
                    error={!isEmpty(rule.error)}
                    value={minValue == null ? "" : minValue}
                    type="number"
                    onChange={(ev) => onUpdateSmartTradeRule(ev.target)}
                    slotProps={{
                      input: {
                        classes: { input: "rule-input-field" },
                        startAdornment: <InputAdornment style={{ whiteSpace: "nowrap" }} position="start">{rule.unit}</InputAdornment>,
                      },

                      htmlInput: {
                        step: 0.1,
                        max: maxValue,
                      },

                      formHelperText: { error: true },
                    }}
                />}
            </div>
          </div>
          <div className="middle">-</div>
          <div className="maximum">
            <div className="rule-input">
              {rule.category === "factor"
                ? <FactorConstraintInput {...{
                    id: "max",
                    autoFocus: true,
                    initialValue: maxValue,
                    onChange: onUpdateSmartTradeRule,
                    supportMultiplierSuffix: rule.isCurrency,
                  }}
                />
                : <TextField
                    variant="standard"
                    id="max"
                    error={!isEmpty(rule.error)}
                    value={maxValue == null ? "" : maxValue}
                    disabled={isReadOnly}
                    type="number"
                    onChange={(ev) => onUpdateSmartTradeRule(ev.target)}
                    slotProps={{
                      input: {
                        classes: { input: "rule-input-field" },
                        startAdornment: <InputAdornment style={{ whiteSpace: "nowrap" }} position="start">{rule.unit}</InputAdornment>,
                      },

                      htmlInput: {
                        step: 0.1,
                        min: minValue,
                      },
                    }}
                />}
            </div>
          </div>
        </div>
        {rule.error && <div className="input-error">{rule.error}</div>}
      </div>
    </div>
  </div>
}

Rule.propTypes = {
  rule: SMART_TRADE_RULE.isRequired,
  removeSmartTradeRule: PropTypes.func.isRequired,
  updateSmartTradeRule: PropTypes.func.isRequired,
  currentValue: PropTypes.string,
  isReadOnly: PropTypes.bool.isRequired,
  smartLabel: PropTypes.string,
}
